/**
 * State machine for the Become a Partner form (COM-737). Owns the submit, the
 * double-submit guard and the post-success lock; the organism only renders
 * `status` / `locked`. Field values and validation belong to RVF.
 */
import { useCallback, useRef, useState } from "react";

import { submitPartnerForm } from "./partner-form.client";
import { statusForPartnerResult } from "./partner-form.logic";
import type {
  PartnerFormStatus,
  PartnerFormValues,
  UsePartnerFormOptions,
  UsePartnerFormResult,
} from "./types";

export function usePartnerForm({
  enabled,
}: UsePartnerFormOptions): UsePartnerFormResult {
  const [status, setStatus] = useState<PartnerFormStatus>({ kind: "idle" });
  const [formKey, setFormKey] = useState(0);
  // Same-tick double-click guard: `status` flips on the next commit, and each
  // post is a real email to the partnerships inbox (AC: no duplicates). The one
  // accepted gap (as COM-735): a send that times out Worker-side may already
  // have gone out, and the retry message then invites a duplicate.
  const inFlight = useRef(false);
  const locked = status.kind === "succeeded";

  const submit = useCallback(
    async (values: PartnerFormValues) => {
      if (!enabled || inFlight.current || locked) return;
      inFlight.current = true;
      setStatus({ kind: "submitting" });
      try {
        const result = await submitPartnerForm(values);
        // Clear the fields ONLY on success; a failure keeps everything typed.
        if (result.ok) setFormKey(key => key + 1);
        setStatus(statusForPartnerResult(result));
      } catch {
        // The client never throws by contract; this only stops a broken
        // contract stranding the button in `submitting`.
        setStatus({ kind: "failed", reason: "unavailable" });
      } finally {
        inFlight.current = false;
      }
    },
    [enabled, locked],
  );

  return { status, formKey, locked, submit };
}
